/**
 * Galerie de fin de partie : une carte par retouche, miniature composee en
 * idle, tap pour ouvrir le replay plein ecran.
 */
import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { collabSlot, isCollab, type GalleryItem, type RoomSnapshot } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { playerById, useRoomStore } from '../store/roomStore.js';
import { paintings } from '../game/artwork.js';
import { resolveZone } from '@retusch/shared';
import { Avatar } from '../ui/Avatar.js';
import { useIdleThumbnail } from './useIdleThumbnail.js';
import type { ArtworkLayer, ArtworkRenderInput } from './renderArtwork.js';
import { ReplayOverlay } from './ReplayOverlay.js';
import { ShareButton } from './ShareButton.js';

const THUMBNAIL_SIZE = 320;

/** Resout paintingId + zoneIndex via le catalogue local. */
export function resolveArtwork(
  item: GalleryItem,
  drawing: ArrayBuffer | null,
  layers?: readonly ArtworkLayer[],
): ArtworkRenderInput | null {
  const painting = paintings().find((candidate) => candidate.paintingId === item.paintingId);
  const origin = painting?.drawZones[item.zoneIndex];
  if (painting === undefined || origin === undefined) return null;
  // Le catalogue ne donne qu'une origine : la taille vient de la partie.
  return {
    file: painting.file,
    zone: resolveZone(origin, item.zoneSize),
    palette: item.palette,
    // Tableau collaboratif : les COUCHES priment sur le blob. Le serveur
    // n'archive que la derniere (il ne decode jamais un dessin, il ne peut
    // pas les fusionner) : rendue seule, on ne voyait que la derniere
    // retouche, jamais l'œuvre entiere.
    drawing: layers !== undefined && layers.length > 0 ? null : drawing,
    ...(layers === undefined ? {} : { layers }),
  };
}

/**
 * Les couches d'un tableau collaboratif, pour la galerie.
 *
 * Reconstruites depuis l'etat du VOTE, qui vit encore en fin de partie : le
 * serveur n'a que des blobs opaques, seul le client peut recomposer l'œuvre.
 * En collaboratif, `item.slot` EST l'indice du tableau, l'archivage les pousse
 * dans l'ordre.
 */
function collabLayersFor(
  collab: ReturnType<typeof useRoomStore.getState>['collab'],
  item: GalleryItem,
): readonly ArtworkLayer[] | undefined {
  const board = collab?.boards[item.slot];
  if (collab === null || board === undefined) return undefined;
  return board.authors.map((_, order) => ({
    drawing: collab.drawings[collabSlot(board.slot, order)] ?? null,
    palette: board.palettes[order] ?? item.palette,
  }));
}

export function Gallery({ room }: { room: RoomSnapshot }): ReactElement | null {
  const t = useT();
  const gallery = useRoomStore((state) => state.gallery);
  // L'etat du vote collaboratif, encore vivant en fin de partie : c'est lui
  // qui porte les couches, le serveur n'archive que des blobs opaques.
  const collab = useRoomStore((state) => state.collab);
  const collabMode = isCollab(room.settings.mode);
  const [openSlot, setOpenSlot] = useState<number | null>(null);

  const close = useCallback(() => setOpenSlot(null), []);

  if (gallery === null || gallery.items.length === 0) {
    return (
      <section className="gallery">
        <p className="section-label">{t('gallery.title')}</p>
        <p className="hint">{t('gallery.loading')}</p>
      </section>
    );
  }

  const openItem = gallery.items.find((item) => item.slot === openSlot) ?? null;

  return (
    <section className="gallery">
      <div className="gallery__header">
        <p className="section-label">{t('gallery.title')}</p>
        <ShareButton />
      </div>

      <div className="gallery__grid">
        {gallery.items.map((item) => (
          <ArtworkCard
            key={item.slot}
            item={item}
            drawing={gallery.drawings[item.slot] ?? null}
            layers={collabMode ? collabLayersFor(collab, item) : undefined}
            room={room}
            onOpen={() => setOpenSlot(item.slot)}
          />
        ))}
      </div>

      {openItem !== null && (
        <ReplayOverlay
          item={openItem}
          drawing={gallery.drawings[openItem.slot] ?? null}
          layers={collabMode ? collabLayersFor(collab, openItem) : undefined}
          room={room}
          onClose={close}
        />
      )}
    </section>
  );
}

function ArtworkCard({
  item,
  drawing,
  layers,
  room,
  onOpen,
}: {
  item: GalleryItem;
  drawing: ArrayBuffer | null;
  layers?: readonly ArtworkLayer[] | undefined;
  room: RoomSnapshot;
  onOpen: () => void;
}): ReactElement {
  const t = useT();
  const drawer = playerById(room, item.drawerId);

  // L'entree ne devient calculable qu'une fois le blob arrive : jusque-la, la
  // carte reste en chargement plutot que d'afficher une zone vide trompeuse.
  const input = useMemo(
    () => (drawing === null ? null : resolveArtwork(item, drawing, layers)),
    [item, drawing, layers],
  );
  const thumbnail = useIdleThumbnail(input, THUMBNAIL_SIZE);

  const found = item.hit && item.elapsedMs !== null;

  return (
    <button type="button" className="artwork-card" onClick={onOpen}>
      <span className="artwork-card__frame">
        {thumbnail.status === 'ready' ? (
          <img className="artwork-card__image" src={thumbnail.dataUrl} alt="" />
        ) : (
          <span className="artwork-card__skeleton" data-error={thumbnail.status === 'error'} />
        )}
      </span>

      <span className="artwork-card__meta">
        {drawer !== null && (
          <Avatar playerId={drawer.id} avatar={drawer.avatar} colorIndex={drawer.colorIndex} size={22} />
        )}
        <span className="artwork-card__name">{drawer?.nickname ?? '?'}</span>
        <span className="artwork-card__time mono-tabular" data-found={found}>
          {found
            ? t('gallery.foundIn', { seconds: ((item.elapsedMs ?? 0) / 1000).toFixed(1) })
            : t('gallery.neverFound')}
        </span>
      </span>
    </button>
  );
}
